import { useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'sonner';

import { CalculatorCalendar } from './calculator-calendar';
import { CalculatorLegend } from './calculator-legend';
import { combineDateAndTime, formatCivilDate, instantToCivilUtc } from './civil-date';
import { CivilDatePicker } from './civil-date-picker';
import { CountingReference } from './counting-reference';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import {
  resolveProceduralDate,
  type ResolveProceduralDateResult,
} from '../../../../../backend/src/matter/procedural-timeline/date-resolution';
import { useUpdateProceduralEvent } from '@/api/mutations/useUpdateProceduralEvent';
import { useGetProceduralTimelineSuggestions } from '@/api/queries/useGetProceduralTimelineSuggestions';
import { useProceduralHolidays } from '@/api/queries/useProceduralHolidays';
import { usePermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { COUNTING_OPTIONS, CountingKey, useTimelineCalculatorStore } from '@/new/stores/timeline-calculator-store';

const POPOVER_WIDTH = 480;
// Distance from the bottom (px) within which the scroll area counts as "read".
const SCROLL_BOTTOM_THRESHOLD = 8;

export function TimelineCalculator() {
  const prefill = useTimelineCalculatorStore((s) => s.prefill);
  const position = useTimelineCalculatorStore((s) => s.position);
  const close = useTimelineCalculatorStore((s) => s.close);
  const setPosition = useTimelineCalculatorStore((s) => s.setPosition);

  if (prefill === null) return null;

  return (
    <CalculatorBody
      key={`${prefill.eventId ?? 'new'}-${prefill.matterId}`}
      position={position}
      onPositionChange={setPosition}
      onClose={close}
      prefill={prefill}
    />
  );
}

interface BodyProps {
  position: { x: number; y: number };
  onPositionChange: (x: number, y: number) => void;
  onClose: () => void;
  prefill: NonNullable<ReturnType<typeof useTimelineCalculatorStore.getState>['prefill']>;
}

function CalculatorBody({ position, onPositionChange, onClose, prefill }: BodyProps) {
  const [startDate, setStartDate] = useState<Date | null>(instantToCivilUtc(prefill.startDate));
  const [days, setDays] = useState<number>(prefill.days);
  const [counting, setCounting] = useState<CountingKey>(prefill.counting);
  const [result, setResult] = useState<ResolveProceduralDateResult | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [endDate, setEndDate] = useState<Date | null>(instantToCivilUtc(prefill.endDate));
  const [endTime, setEndTime] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const option = useMemo(() => COUNTING_OPTIONS.find((o) => o.key === counting) ?? COUNTING_OPTIONS[0], [counting]);

  const yearRange = useMemo(() => {
    const anchorYear = startDate?.getUTCFullYear() ?? new Date().getUTCFullYear();
    return { fromYear: anchorYear - 1, toYear: anchorYear + 2 };
  }, [startDate]);

  const { data: holidays } = useProceduralHolidays({
    matterId: prefill.matterId,
    jurisdiction: option.jurisdiction,
    fromYear: yearRange.fromYear,
    toYear: yearRange.toYear,
  });

  const updateEvent = useUpdateProceduralEvent();
  const { allowed: canEdit } = usePermission(Permission.ProceduralTimelineEdit);
  const { data: pendingSuggestions } = useGetProceduralTimelineSuggestions(prefill.matterId);
  const hasPendingSuggestions = (pendingSuggestions?.length ?? 0) > 0;

  const [recalculateDependents, setRecalculateDependents] = useState(true);
  const showDependentToggle = prefill.eventId !== null && prefill.hasDependents;

  // Gate the update until the user has scrolled through the calculation.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el === null) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD) setScrolledToEnd(true);
  };

  useEffect(() => {
    if (result === null) {
      setScrolledToEnd(false);
      return;
    }
    const el = scrollRef.current;
    if (el === null) return;
    setScrolledToEnd(el.scrollHeight - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD);
  }, [result]);

  const invalidateResult = () => {
    setResult(null);
    setSelectedDate(null);
    setSelectedTime('');
    setError(null);
  };

  const handleStartDateChange = (d: Date | null) => {
    setStartDate(d);
    invalidateResult();
  };

  const handleDaysChange = (n: number) => {
    setDays(n);
    invalidateResult();
  };

  const handleCountingChange = (k: CountingKey) => {
    setCounting(k);
    invalidateResult();
  };

  const handleCalculate = () => {
    setError(null);
    if (startDate === null) {
      setError('Pick a source date first.');
      return;
    }
    if (!Number.isFinite(days) || days === 0) {
      setError('Enter a non-zero number of days elapsed.');
      return;
    }
    if (holidays === undefined) {
      setError('Loading holidays, try again in a moment.');
      return;
    }
    try {
      const r = resolveProceduralDate({
        startDate,
        interval: { days },
        jurisdiction: option.jurisdiction,
        dayCount: option.dayCount,
        holidays,
      });
      setResult(r);
      setSelectedDate(r.resultDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Calculation failed.');
    }
  };

  const handleSubmit = async () => {
    if (prefill.eventId === null) {
      onClose();
      return;
    }
    if (selectedDate === null) {
      setError('Calculate or pick a date first.');
      return;
    }
    const iso = combineDateAndTime(selectedDate, selectedTime);
    if (endDate !== null && endDate.getTime() < selectedDate.getTime()) {
      setError('End date must be on or after the result date.');
      return;
    }
    const endIso = endDate !== null ? combineDateAndTime(endDate, endTime) : undefined;
    try {
      await updateEvent.mutateAsync({
        matterId: prefill.matterId,
        eventId: prefill.eventId,
        input: {
          startDateTime: iso,
          ...(endIso !== undefined ? { endDateTime: endIso } : {}),
          recalculateDependents,
        },
      });
      toast.success('Event date updated.');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update event.');
    }
  };

  return (
    <div
      className="fixed z-50 flex max-h-[90vh] w-[480px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
      style={{ left: position.x, top: position.y, width: POPOVER_WIDTH }}
    >
      <DragHandle
        position={position}
        onPositionChange={onPositionChange}
        onClose={onClose}
        title="Timeline calculator"
        subtitle={prefill.eventName ?? 'New calculation'}
      />

      <div ref={scrollRef} onScroll={handleScroll} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        <Inputs
          startDate={startDate}
          onStartDateChange={handleStartDateChange}
          days={days}
          onDaysChange={handleDaysChange}
          counting={counting}
          onCountingChange={handleCountingChange}
          onCalculate={handleCalculate}
        />

        {error !== null && (
          <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>
        )}

        {result !== null && startDate !== null && (
          <ResultBlock
            result={result}
            startDate={startDate}
            selectedDate={selectedDate ?? result.resultDate}
            onSelectDate={(d) => setSelectedDate(d)}
            selectedTime={selectedTime}
            onSelectedTimeChange={setSelectedTime}
            endDate={endDate}
            onEndDateChange={setEndDate}
            endTime={endTime}
            onEndTimeChange={setEndTime}
            canEdit={canEdit}
            hasPendingSuggestions={hasPendingSuggestions}
          />
        )}

        {result !== null && showDependentToggle && (
          <div className="flex items-center gap-3">
            <Switch
              id="calc-recalc-dependents"
              checked={recalculateDependents}
              onCheckedChange={setRecalculateDependents}
              disabled={!canEdit}
            />
            <div className="space-y-1">
              <label
                htmlFor="calc-recalc-dependents"
                className="block cursor-pointer text-sm font-medium text-gray-800"
              >
                Suggest updates to dependents
              </label>
              <p className="text-xs text-gray-500">
                Recalculate the dates of events that follow on from this one and surface them as suggestions for review.
                No events will be directly updated other than the current event.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 p-3">
        <span className="text-xs text-gray-400">
          {result !== null && !scrolledToEnd ? 'Scroll to review the full calculation.' : ''}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmit}
            disabled={
              !canEdit ||
              hasPendingSuggestions ||
              updateEvent.isLoading ||
              result === null ||
              prefill.eventId === null ||
              selectedDate === null ||
              !scrolledToEnd
            }
          >
            {updateEvent.isLoading ? (
              <Icon className="lucide--loader-2 animate-spin" />
            ) : (
              <Icon className="lucide--check" />
            )}
            Update event
          </Button>
        </div>
      </div>
    </div>
  );
}

interface InputsProps {
  startDate: Date | null;
  onStartDateChange: (d: Date | null) => void;
  days: number;
  onDaysChange: (d: number) => void;
  counting: CountingKey;
  onCountingChange: (k: CountingKey) => void;
  onCalculate: () => void;
}

function Inputs({
  startDate,
  onStartDateChange,
  days,
  onDaysChange,
  counting,
  onCountingChange,
  onCalculate,
}: InputsProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Source date</Label>
          <CivilDatePicker value={startDate} onChange={onStartDateChange} />
        </div>
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Days elapsed</Label>
          <DaysInput days={days} onDaysChange={onDaysChange} />
        </div>
      </div>
      <div>
        <div className="mb-1 flex items-center gap-1.5">
          <Label className="mb-0 text-xs font-medium text-gray-700">Counting method</Label>
          <CountingReference />
        </div>
        <Select value={counting} onValueChange={(v) => onCountingChange(v as CountingKey)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTING_OPTIONS.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button variant="primary" type="button" onClick={onCalculate} className="w-full">
        Calculate
      </Button>
    </div>
  );
}

const DAYS_LIMIT = 365;

function DaysInput({ days, onDaysChange }: { days: number; onDaysChange: (n: number) => void }) {
  const [text, setText] = useState<string>(days === 0 ? '' : String(clamp(days)));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const parsed = parseInt(v, 10);
    if (Number.isFinite(parsed)) {
      const clamped = clamp(parsed);
      setText(String(clamped));
      onDaysChange(clamped);
    } else {
      setText(v);
      onDaysChange(0);
    }
  };

  const handleBlur = () => {
    if (text === '' || text === '-') {
      setText('0');
      onDaysChange(0);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (text === '0') setText('');
    const target = e.currentTarget;
    setTimeout(() => target.select(), 0);
  };

  return (
    <Input
      type="number"
      min={-DAYS_LIMIT}
      max={DAYS_LIMIT}
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className="w-full"
    />
  );
}

function clamp(n: number): number {
  if (n > DAYS_LIMIT) return DAYS_LIMIT;
  if (n < -DAYS_LIMIT) return -DAYS_LIMIT;
  return n;
}

interface ResultBlockProps {
  result: ResolveProceduralDateResult;
  startDate: Date;
  selectedDate: Date;
  onSelectDate: (d: Date) => void;
  selectedTime: string;
  onSelectedTimeChange: (t: string) => void;
  endDate: Date | null;
  onEndDateChange: (d: Date | null) => void;
  endTime: string;
  onEndTimeChange: (t: string) => void;
  canEdit: boolean;
  hasPendingSuggestions: boolean;
}

function ResultBlock({
  result,
  startDate,
  selectedDate,
  onSelectDate,
  selectedTime,
  onSelectedTimeChange,
  endDate,
  onEndDateChange,
  endTime,
  onEndTimeChange,
  canEdit,
  hasPendingSuggestions,
}: ResultBlockProps) {
  return (
    <div className="flex flex-col gap-3">
      <CalculatorLegend result={result} />
      <CalculatorCalendar
        result={result}
        startDate={startDate}
        selectedDate={selectedDate}
        onSelectDate={onSelectDate}
      />

      <div className="flex flex-col gap-1 text-xs text-gray-700">
        {result.startDateExcluded && (
          <p>
            <span className="text-gray-400">↳ </span>
            <strong>Day 0, {formatCivilDate(startDate, 'EEEE d MMMM yyyy')}</strong>: start day, not counted.
          </p>
        )}
        <p>
          <span className="text-gray-400">↳ </span>
          Counted {result.countedDates.length} {labelForCounting(result, result.countedDates.length)} between the start
          and the result.
        </p>
        {result.endDateRolledForward && (
          <p>
            <span className="text-gray-400">↳ </span>
            {result.direction === 'before'
              ? 'Result moved back to the previous business day.'
              : 'Result rolled forward to the next business day.'}
          </p>
        )}
        <p className="text-teal-700">
          <span className="text-gray-400">↳ </span>
          <strong>Result: {formatCivilDate(result.resultDate, 'EEEE d MMMM yyyy')}.</strong>
        </p>
        {result.warnings.map((w) => (
          <p key={w} className="text-amber-700">
            <span className="text-amber-500">↳ </span>
            {w}
          </p>
        ))}
      </div>

      {!canEdit && (
        <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          View only. You don't have permission to update this event.
        </p>
      )}

      {canEdit && hasPendingSuggestions && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          There are pending suggestions. Accept or reject them before updating this event.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Event start date</Label>
          <CivilDatePicker value={selectedDate} onChange={(d) => d !== null && onSelectDate(d)} disabled={!canEdit} />
        </div>
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">Start time (optional)</Label>
          <Input
            type="time"
            value={selectedTime}
            onChange={(e) => onSelectedTimeChange(e.target.value)}
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">End date (optional)</Label>
          <CivilDatePicker
            value={endDate}
            onChange={onEndDateChange}
            clearable
            placeholder="Pick a date"
            disabled={!canEdit}
          />
        </div>
        <div>
          <Label className="mb-1 block text-xs font-medium text-gray-700">End time (optional)</Label>
          <Input
            type="time"
            value={endTime}
            onChange={(e) => onEndTimeChange(e.target.value)}
            disabled={!canEdit || endDate === null}
          />
        </div>
      </div>
    </div>
  );
}

interface DragHandleProps {
  position: { x: number; y: number };
  onPositionChange: (x: number, y: number) => void;
  onClose: () => void;
  title: string;
  subtitle: string;
}

function DragHandle({ position, onPositionChange, onClose, title, subtitle }: DragHandleProps) {
  const dragState = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - position.x,
      offsetY: e.clientY - position.y,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragState.current === null || dragState.current.pointerId !== e.pointerId) return;
    const nextX = Math.max(0, Math.min(window.innerWidth - POPOVER_WIDTH, e.clientX - dragState.current.offsetX));
    const nextY = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragState.current.offsetY));
    onPositionChange(nextX, nextY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragState.current?.pointerId === e.pointerId) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      dragState.current = null;
    }
  };

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className="flex shrink-0 cursor-grab items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2 select-none active:cursor-grabbing"
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <Icon className="lucide--grip-vertical text-gray-400" />
        <div className="overflow-hidden">
          <p className="truncate text-sm font-medium text-gray-900">{title}</p>
          <p className="truncate text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="About the timeline calculator"
            >
              <Icon className="lucide--info" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="max-w-xs text-left leading-relaxed">
            Calculate a procedural deadline from an anchor date. Pick a source date, the number of days elapsed, and a
            counting method. The calculator resolves the deadline using bank-holiday data for the chosen jurisdiction
            and shows which days counted and which were skipped. Verify and adjust the result before applying it to the
            event.
          </TooltipContent>
        </Tooltip>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          aria-label="Close calculator"
        >
          <Icon className="lucide--x" />
        </Button>
      </div>
    </div>
  );
}

function labelForCounting(result: ResolveProceduralDateResult, count: number): string {
  const noun = Math.abs(count) === 1 ? 'day' : 'days';
  switch (result.dayCount) {
    case 'calendar':
      return `calendar ${noun}`;
    case 'business':
      return `business ${noun}`;
    case 'clear-cpr':
      return `clear ${noun}`;
  }
}
